import { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import useAuth from './useAuth';
import logger from '../utils/logger';
import { getSocketUrl, getSocketOptions } from '../utils/socketConfig';

/**
 * Hook for real-time collaboration on meeting notes.
 * Single connection per token; cleanup on unmount prevents duplicate connections under StrictMode.
 */
export function useMeetingNoteSocket(noteId) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const currentUserId = auth?.employee?._id;
  const [isConnected, setIsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const socketRef = useRef(null);
  const contentUpdateListenersRef = useRef(new Set());
  const noteIdRef = useRef(noteId);
  const currentUserIdRef = useRef(currentUserId);
  const mountedRef = useRef(true);

  useEffect(() => {
    noteIdRef.current = noteId;
    currentUserIdRef.current = currentUserId;
  }, [noteId, currentUserId]);

  useEffect(() => {
    mountedRef.current = true;

    if (!token || !noteId || !token.trim()) {
      if (socketRef.current) {
        socketRef.current.emit('meeting-note:leave', noteIdRef.current);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setActiveUsers([]);
      return;
    }

    if (socketRef.current) {
      if (socketRef.current.connected) {
        const oldNoteId = noteIdRef.current;
        noteIdRef.current = noteId;

        if (oldNoteId && oldNoteId !== noteId) {
          socketRef.current.emit('meeting-note:leave', oldNoteId);
        }

        socketRef.current.emit('meeting-note:join', noteId, (res) => {
          if (!mountedRef.current) return;
          if (res?.users && Array.isArray(res.users)) {
            setActiveUsers(res.users);
          }
        });
        return;
      }

      const existingSocket = socketRef.current;
      noteIdRef.current = noteId;

      existingSocket.once('connect', () => {
        if (!mountedRef.current) return;
        existingSocket.emit('meeting-note:join', noteId, (res) => {
          if (!mountedRef.current) return;
          if (res?.users && Array.isArray(res.users)) {
            setActiveUsers(res.users);
          }
        });
      });
      return;
    }

    const socket = io(getSocketUrl(), getSocketOptions(token, {
      forceNew: false,
    }));
    socketRef.current = socket;
    noteIdRef.current = noteId;

    socket.on('connect', () => {
      if (!mountedRef.current) return;
      setIsConnected(true);

      if (!noteIdRef.current) return;

      setTimeout(() => {
        if (!mountedRef.current || !socketRef.current || socketRef.current !== socket) return;
        if (!socket.connected) return;

        const noteIdToJoin = noteIdRef.current;
        try {
          socket.emit('meeting-note:join', noteIdToJoin, (res) => {
            if (!mountedRef.current) return;
            if (res?.error) {
              logger.error('[useMeetingNoteSocket] Join error:', res.error);
              setActiveUsers([]);
            } else if (res?.users && Array.isArray(res.users)) {
              setActiveUsers(res.users);
            }
          });
        } catch (emitError) {
          logger.error('[useMeetingNoteSocket] Emit join error:', emitError);
        }
      }, 200);
    });

    socket.on('connect_error', (error) => {
      if (mountedRef.current) {
        logger.error('[useMeetingNoteSocket] Connection error:', error.message);
        setIsConnected(false);
      }
    });

    socket.on('error', (error) => {
      if (mountedRef.current) logger.error('[useMeetingNoteSocket] Socket error:', error);
    });

    socket.on('disconnect', () => {
      if (mountedRef.current) setIsConnected(false);
    });

    socket.on('reconnect', () => {
      if (!mountedRef.current) return;
      setIsConnected(true);
      socket.emit('meeting-note:join', noteIdRef.current, (res) => {
        if (!mountedRef.current) return;
        if (res?.error) {
          logger.error('[useMeetingNoteSocket] Rejoin error:', res.error);
        } else if (res?.users) {
          setActiveUsers(res.users);
        }
      });
    });

    socket.on('meeting-note:users', (payload) => {
      if (!mountedRef.current) return;
      if (payload?.users && Array.isArray(payload.users)) {
        setActiveUsers(payload.users);
      }
    });

    socket.on('meeting-note:content-updated', (payload) => {
      const payloadUserId = payload?.updatedBy?.toString();
      const currentUserIdStr = currentUserIdRef.current?.toString();

      if (payloadUserId && currentUserIdStr && payloadUserId === currentUserIdStr) {
        return;
      }

      contentUpdateListenersRef.current.forEach((cb) => {
        try {
          cb(payload);
        } catch (e) {
          logger.error('[useMeetingNoteSocket] content-updated listener error:', e);
        }
      });
    });

    return () => {
      mountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.emit('meeting-note:leave', noteIdRef.current);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setActiveUsers([]);
    };
  }, [token, noteId]);

  const sendContentUpdate = useCallback((tabs, activeTabIndex) => {
    if (!socketRef.current || !noteId) return;

    socketRef.current.emit(
      'meeting-note:content-change',
      { noteId, tabs, activeTabIndex },
      (res) => {
        if (res?.error) logger.error('[useMeetingNoteSocket] content-change error:', res.error);
      }
    );
  }, [noteId]);

  const onContentUpdate = useCallback((callback) => {
    contentUpdateListenersRef.current.add(callback);
    return () => {
      contentUpdateListenersRef.current.delete(callback);
    };
  }, []);

  return {
    isConnected,
    activeUsers,
    sendContentUpdate,
    onContentUpdate,
  };
}

export default useMeetingNoteSocket;
